import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from '../utils.ts';

interface InputProps extends ComponentPropsWithoutRef<'input'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Slot rendered inside the input on the right (e.g., copy button). */
  trailing?: ReactNode;
  /** Slot rendered inside the input on the left (e.g., search icon). */
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `input-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="text-caption uppercase tracking-[0.08em] font-medium text-cool-gray"
        >
          {label}
        </label>
      ) : null}
      <div className="relative">
        {leading ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 grid place-items-center pl-4 text-cool-gray"
          >
            {leading}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'block w-full h-12 rounded-input border bg-surface text-midnight placeholder:text-cool-gray',
            'transition-all duration-fast ease-clickt',
            'focus-visible:outline-none focus-visible:shadow-focus-ring focus-visible:border-electric',
            'disabled:bg-light-silver/60 disabled:text-cool-gray disabled:cursor-not-allowed',
            error
              ? 'border-danger focus-visible:border-danger focus-visible:shadow-[0_0_0_4px_rgba(255,77,90,0.22)]'
              : 'border-border-subtle',
            leading ? 'pl-11' : 'pl-4',
            trailing ? 'pr-11' : 'pr-4',
            className,
          )}
          {...rest}
        />
        {trailing ? (
          <span className="absolute inset-y-0 right-0 grid place-items-center pr-2">
            {trailing}
          </span>
        ) : null}
      </div>
      {hint && !error ? (
        <p id={hintId} className="text-[13px] text-cool-gray leading-snug">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-[13px] text-danger leading-snug">
          {error}
        </p>
      ) : null}
    </div>
  );
});
